/**
 * Wizard - multi-step forms on top of validator + former.
 *
 * The wizard owns navigation, the progress header and per-step gating; the
 * field rules stay in validator and the final submit stays in former. Only the
 * current step is visible, so validator (which skips non-rendered fields)
 * naturally validates just that step on each Next/Finish, while former.values()
 * still collects every step for the submit.
 *
 * Navigation contract: FORWARD always validates (a submit routes through
 * validator; we advance only on validator:submit = the visible step is valid),
 * BACK is free (Back button + clicking a passed step in the header).
 */
const g = () => window.g;

export class Wizard {
	constructor(form, opts) {
		this.form = form;
		this.opts = opts || {};
		form.wizard = this;
		if (!form.hasAttribute('wizard')) form.setAttribute('wizard', ''); // so former defers its submit to us
		form.classList.add('wizard');
		if (this.opts.vertical) form.classList.add('wizard-vertical');

		this.steps = Array.from(form.querySelectorAll('[wizard-step]'));
		this.steps.forEach((s) => s.classList.add('wizard-step'));
		this.current = 0;
		this.maxReached = 0;
		this.completed = false;

		if (!this.steps.length) return;
		this.buildHeader();
		this.buildFooter();
		this.bind();
		this.show(Math.max(0, Math.min(this.steps.length - 1, this.opts.startStep || 0)), true);
	}

	validator() { return this.form.validator || null; }

	buildHeader() {
		if (this.opts.header === false) return;
		const header = document.createElement('div');
		header.className = 'wizard-header';
		this.headerItems = this.steps.map((step, i) => {
			const title = step.getAttribute('step-title') || g().translate('Step {n}', { n: i + 1 });
			const icon = step.getAttribute('step-icon') || '';
			const marker = icon ? '<i class="' + icon + '"></i>' : '<span class="wizard-num">' + (i + 1) + '</span>';
			const tab = document.createElement('button');
			tab.type = 'button';
			tab.className = 'wizard-tab';
			tab.innerHTML = '<span class="wizard-marker">' + marker + '<i class="ri-check-line wizard-check"></i></span><span class="wizard-tab-label">' + g().esc(title) + '</span>';
			tab.addEventListener('click', () => { if (i < this.current) this.show(i); }); // header navigates back only
			header.appendChild(tab);
			return tab;
		});
		this.form.insertBefore(header, this.form.firstChild);
	}

	buildFooter() {
		const q = (sel) => this.form.querySelector(sel);
		const authored = { back: q('[data-wizard-back]'), next: q('[data-wizard-next]'), finish: q('[data-wizard-finish]') };
		if (authored.back || authored.next || authored.finish || this.opts.footer === false) {
			this.authored = authored;
			return;
		}
		const label = (opt, attr, fallback) => this.opts[opt] || this.form.getAttribute(attr) || g().translate(fallback);
		const footer = document.createElement('div');
		footer.className = 'wizard-footer';
		const back = document.createElement('button');
		back.type = 'button'; back.className = 'btn btn-secondary wizard-back';
		back.innerHTML = '<i class="ri-arrow-left-line me-1"></i>' + g().esc(label('backLabel', 'wizard-back-label', 'Back'));
		const next = document.createElement('button');
		next.type = 'submit'; next.className = 'btn btn-primary wizard-next';
		next.innerHTML = g().esc(label('nextLabel', 'wizard-next-label', 'Next')) + '<i class="ri-arrow-right-line ms-1"></i>';
		const finish = document.createElement('button');
		finish.type = 'submit'; finish.className = 'btn btn-success wizard-finish';
		finish.innerHTML = '<i class="ri-check-line me-1"></i>' + g().esc(label('finishLabel', 'wizard-finish-label', 'Finish'));
		footer.append(back, next, finish);
		this.form.appendChild(footer);
		this.footer = { back, next, finish };
	}

	bind() {
		const backEls = [this.footer && this.footer.back, this.authored && this.authored.back].filter(Boolean);
		backEls.forEach((b) => b.addEventListener('click', () => this.back()));
		// Authored Next/Finish that are not submit buttons still trigger a submit
		// (so validator runs); a real type=submit needs no wiring.
		['next', 'finish'].forEach((k) => {
			const el = this.authored && this.authored[k];
			if (el && el.type !== 'submit') el.addEventListener('click', (e) => { e.preventDefault(); this.form.requestSubmit(); });
		});

		// Submit routing. With validator: it prevents the native submit and emits
		// validator:submit ONLY when the visible step is valid -> we advance.
		// Without validator: our own handler advances (nothing to validate).
		this.form.addEventListener('validator:submit', () => this.advance());
		this.form.addEventListener('submit', (e) => { e.preventDefault(); if (!this.validator()) this.advance(); });
		// A server validation outcome (data.validated false) -> jump to the step
		// holding the first bad field.
		this.form.addEventListener('former:error', (e) => {
			const d = e.detail && e.detail.res && e.detail.res.data;
			if (d && d.validated === false && d.fields) this.goToError(d.fields);
		});
		// former saved successfully -> mark the wizard complete (the last step gets
		// its done badge too, useful when the page does not navigate away).
		this.form.addEventListener('former:saved', () => this.complete());
	}

	// Mark every step done (including the last/current one). Called on a
	// successful finish; visual only. `wizard:complete` lets the app react.
	complete() {
		if (this.completed) return;
		this.completed = true;
		this.updateHeader();
		this.emit('wizard:complete', {});
	}

	isLast() { return this.current >= this.steps.length - 1; }

	async advance() {
		if (this.opts.beforeNext) {
			const ok = await this.opts.beforeNext(this.current, this.steps[this.current]);
			if (ok === false) return;
		}
		if (this.isLast()) this.finish();
		else this.show(this.current + 1);
	}

	finish() {
		if (this.form.former) {
			return this.form.former.send().then((res) => {
				const d = res && res.data;
				if (d && d.validated === false && d.fields) this.goToError(d.fields);
				return res;
			});
		}
		const values = g().former ? g().former.values(this.form) : null;
		this.complete();
		this.emit('wizard:finish', { values });
		return Promise.resolve(null);
	}

	back() { if (this.current > 0) this.show(this.current - 1); }

	// Public: jump straight to a step. Forward is refused (forward must validate
	// through Next); back is allowed.
	goTo(i) { if (i < this.current) this.show(i); }

	goToError(fields) {
		for (const name of Object.keys(fields || {})) {
			const el = this.form.querySelector('[name="' + (window.CSS ? CSS.escape(name) : name) + '"]');
			const step = el && el.closest('[wizard-step]');
			const idx = step ? this.steps.indexOf(step) : -1;
			if (idx > -1) { this.show(idx); return; }
		}
	}

	show(i, silent) {
		i = Math.max(0, Math.min(this.steps.length - 1, i));
		this.steps.forEach((s, idx) => { s.hidden = idx !== i; });
		this.current = i;
		this.maxReached = Math.max(this.maxReached, i);
		this.updateHeader();
		this.updateFooter();
		if (silent) return;
		this.emit('wizard:step', { index: i, step: this.steps[i] });
		if (this.opts.onStep) this.opts.onStep(i, this.steps[i]);
		const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		this.form.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest' });
	}

	updateHeader() {
		if (!this.headerItems) return;
		this.headerItems.forEach((tab, idx) => {
			const done = idx === this.current ? this.completed : idx <= this.maxReached;
			tab.classList.toggle('is-current', idx === this.current);
			tab.classList.toggle('is-done', done);
			tab.classList.toggle('is-clickable', idx < this.current);
			tab.setAttribute('aria-current', idx === this.current ? 'step' : 'false');
		});
	}

	updateFooter() {
		const set = (el, hidden) => { if (el) el.hidden = hidden; };
		if (this.footer) {
			set(this.footer.back, this.current === 0);
			set(this.footer.next, this.isLast());
			set(this.footer.finish, !this.isLast());
		}
		if (this.authored) {
			set(this.authored.back, this.current === 0);
			set(this.authored.next, this.isLast());
			set(this.authored.finish, !this.isLast());
		}
	}

	emit(name, detail) {
		this.form.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: { wizard: this, ...detail } }));
	}
}
